<template>
  <!-- eslint-disable max-len -->
  <div class="account">
    <div class="page-topbar">
      <div class="page-topbar__title">
        Account Settings
      </div>
    </div>
    <template v-if="fetching">
      <div class="empty-state">
        <loader :size="50" />
      </div>
    </template>
    <template v-else>
      <div class="account-container">
        <div class="account-heading">
          <div class="account-heading__title">
            Change Password
          </div>
          <template v-if="passwordLoading">
            <loader :size="25" />
          </template>
        </div>
        <div class="flex-grid flex-grid--start">
          <div class="form-container column--two">
            <label class="form-label">Current Password</label>
            <input
              id="current-password-field"
              v-model="password.current"
              type="password"
              class="form-field"
            />
          </div>
          <div class="form-container column--two">
            <label class="form-label">New Password</label>
            <password-field
              :password.sync="password.updated"
              @password-submit="updatePassword"
            />
            <password-validation
              :password="password.updated"
              :is-valid.sync="isValidPassword"
            />
          </div>
        </div>
        <div class="account-container-footer">
          <button
            id="change-password-button"
            class="btn"
            :disabled="disablePasswordSubmit"
            @click="updatePassword"
          >
            Change Password
          </button>
          <div class="account__error">
            {{ passwordError }}
          </div>
        </div>
      </div>
      <div class="account-container">
        <div class="account-heading">
          <div class="account-heading__title">
            Notification Email
          </div>
          <template v-if="emailLoading">
            <loader :size="25" />
          </template>
        </div>
        <div class="flex-grid">
          <div class="column--two">
            <div class="form-container">
              <label class="form-label">Account Email</label>
              <div class="form-field__description">
                Any BioConnect Link related alerts will be sent to this email.
              </div>
              <input
                v-model="accountEmail"
                data-testid="account-email-input"
                class="form-field"
                :disabled="!canUpdateNotificationEmail"
              />
            </div>
            <div class="account-container-footer">
              <div v-tooltip="{ content: updateNotificationEmailTooltip }">
                <button
                  data-testid="update-account-button"
                  class="btn"
                  :disabled="!canUpdateNotificationEmail"
                  @click="updateAccountEmail"
                >
                  Update Email
                </button>
              </div>
              <div class="account__error">
                {{ emailError }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
  <!-- eslint-enable max-len -->
</template>

<script>
import passwordQueries from '@/graphql/password.js'
import accountQueries from '@/graphql/account.js'
import Loader from '@/components/loader.vue'
import PasswordField from '@/components/passwordField.vue'
import REGEX from '@/global/utilities.js'
import PasswordValidation from '@/components/passwordValidation.vue'
import constants from '@/global/constants.js'

export default {
  name: 'AccountDetails',
  components: {Loader, PasswordField, PasswordValidation},
  mixins: [passwordQueries, accountQueries],
  data() {
    return {
      fetching: false,
      passwordLoading: false,
      emailLoading: false,
      isValidPassword: false,
      passwordError: '',
      emailError: '',
      accountEmail: '',
      password: {
        current: '',
        updated: '',
      },
      canUpdateNotificationEmail: false,
    }
  },
  computed: {
    disablePasswordSubmit() {
      return this.passwordLoading || !this.isValidPassword || !this.password.current
    },
    updateNotificationEmailTooltip() {
      return this.canUpdateNotificationEmail ? '' : constants.actionNotAvailableMessage
    },
  },
  created() {
    this.canUpdateNotificationEmail = this.$can('update', 'notification-email')
    this.getAccountDetails()
  },
  methods: {
    async getAccountDetails() {
      try {
        const {listAccount} = await this.listAccountDetails()
        this.accountEmail = listAccount.email
      } catch (error) {
        this.displayAlert('fail', 'Unable to get account details.')
        console.log(error)
      }
    },
    async updatePassword() {
      this.passwordLoading = true
      this.passwordError = ''
      const {current, updated} = this.password
      try {
        await this.updatePasswordMutation(current, updated)
        this.passwordLoading = false
        this.clearPassword()
        this.displayAlert('success', `Changed password for ${this.getUsername()}`)
      } catch (error) {
        this.passwordLoading = false
        this.passwordError = error.message
        this.displayAlert('fail', `Unable to change password for ${this.getUsername()}`)
        console.log(error)
      }
    },
    async updateAccountEmail() {
      if (!this.canUpdateNotificationEmail) {
        return
      }

      this.emailError = ''
      if (REGEX.email.test(this.accountEmail)) {
        try {
          this.emailLoading = true
          await this.updateEmailMutation(this.accountEmail)
          this.emailLoading = false
          this.displayAlert('success', 'Account email updated.')
          this.getAccountDetails()
        } catch (error) {
          this.emailLoading = false
          this.displayAlert('fail', 'Unable to update email.')
          console.log(error)
        }
      } else {
        this.emailError = 'Please provide a valid email.'
      }
    },
    clearPassword() {
      this.password.current = ''
      this.password.updated = ''
      this.errorMessage = ''
    },
  },
}
</script>

<style lang="scss" scoped>
.account {
  &__error {
    max-width: 80%;
    font-weight: 400;
    font-size: $small-font;
    color: $red;
  }
  &-topbar {
    margin-bottom: 2em;
    &__title {
      font-size: $title-font;
    }
  }
  &-container {
    color: $black;
    border: 1px solid $light-grey;
    border-radius: 5px;
    padding: 1em;
    margin-bottom: 2em;
    &-footer {
      @include flex-display();
      @include flex-justify-content(space-between);
      @include flex-align-items(center);
      margin-top: 1em;
    }
  }
  &-heading {
    @include flex-display();
    @include flex-justify-content(space-between);
    font-weight: bold;
    margin-bottom: 2em;
  }
}
</style>
